import {useEffect, useState, type ReactNode} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ChevronLeft, Menu as MenuIcon, UserRound} from 'lucide-react';
import {Button, ChekinMark, TopBar, cn} from '@chekinapp/ui';
import {MobileMenuDrawer} from 'components/MobileMenuDrawer';
import {useSplashCoverStore} from 'components/SplashScreen/useSplashCoverStore';
import {useBrandingTheme} from 'context/brandingTheme';
import {useSummary} from 'context/summary';
import {useGuardedNavigate} from 'context/navigationGuard';
import {useAuth} from 'hooks/auth/useAuth';
import {usePath} from 'hooks/common';
import {AUTH_LOGIN_ROUTE, PATHS} from 'routes/constants';
import {getInitials} from 'utils/common';

type Props = {
  className?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  onBack?: () => void;
  hideBack?: boolean;
  rightAddon?: ReactNode;
  propertyName?: string;
  busy?: boolean;
  titleAsHeading?: boolean;
};

export function MobileTopBar({
  className,
  title,
  subtitle,
  onBack,
  hideBack,
  rightAddon,
  propertyName,
  busy,
  titleAsHeading,
}: Props) {
  const {t} = useTranslation();
  const {pathname} = useLocation();
  const {data: template} = useBrandingTheme();
  const {user, isLoggedIn, isInitialLoading} = useAuth();
  const {isInstantCheckInEnabled} = useSummary();
  const navigate = useNavigate();
  const guardedNavigate = useGuardedNavigate();
  const {getFullPath} = usePath();
  const splashCover = useSplashCoverStore(state => state.cover);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const accountInitials = getInitials(user?.email || '');

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  const isPage = Boolean(onBack || title);
  const showLogin =
    !isPage && !isLoggedIn && !isInitialLoading && isInstantCheckInEnabled;
  const brandingLogo = template?.navigation_title_logo;
  const accountAction = !isPage && isLoggedIn;
  const hasRightContent = isPage ? Boolean(rightAddon) : accountAction || showLogin;

  const leading = isPage ? (
    !hideBack && (
      <TopBar.Button aria-label={t('back')} onClick={onBack}>
        <ChevronLeft strokeWidth={2} />
      </TopBar.Button>
    )
  ) : (
    <>
      <TopBar.Button aria-label={t('menu')} onClick={() => setDrawerOpen(true)}>
        <MenuIcon strokeWidth={2} />
      </TopBar.Button>
      <MobileMenuDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </>
  );

  const brandMark = (
    <button
      type="button"
      aria-label={t('home')}
      onClick={() => guardedNavigate(getFullPath(PATHS.home))}
      className="flex shrink-0 cursor-pointer items-center rounded-[12px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--chekin-color-brand-blue)]"
    >
      {brandingLogo ? (
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-[12px]',
            splashCover === 'covering' && 'opacity-0',
            splashCover === 'revealing' &&
              '[transition:opacity_0.2s_ease_0.22s] motion-reduce:transition-none',
          )}
        >
          <img src={brandingLogo} alt="" className="size-full object-contain" />
        </span>
      ) : (
        <ChekinMark className="mr-2 h-auto w-8 shrink-0" />
      )}
    </button>
  );

  const trailing = (
    <>
      {isPage && rightAddon}
      {accountAction && (
        <Button
          variant="secondary"
          size="icon"
          shape="pill"
          aria-label={t('account')}
          onClick={() => setDrawerOpen(true)}
          className="size-[34px] shrink-0 text-[12px] font-bold"
        >
          {accountInitials || <UserRound size={15} strokeWidth={2} />}
        </Button>
      )}
      {showLogin && (
        <Button
          variant="secondary"
          size="s"
          shape="pill"
          onClick={() => navigate(getFullPath(AUTH_LOGIN_ROUTE))}
          className="h-[34px] shrink-0 text-[12px] font-bold"
        >
          {t('auth.login')}
        </Button>
      )}
      {!hasRightContent && brandMark}
    </>
  );

  return (
    <TopBar
      className={cn('lg:hidden', className)}
      leading={leading}
      title={isPage ? title : propertyName || t('your_stay')}
      subtitle={isPage ? subtitle : undefined}
      titleAs={titleAsHeading ? 'h1' : 'span'}
      trailing={trailing}
      busy={busy}
    />
  );
}
